import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { axiosClientAdmin } from '@/lib/auth';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/hooks/use-toast';
import { addUserToTeam } from './AddTeamMemberModal';

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  onUserCreatedAndAdded: () => void;
  initialFirstName?: string;
  initialLastName?: string;
  initialEmail?: string;
}

// API function to create a new user
async function createUser(userData: {
  firstName: string;
  lastName: string;
  email: string;
  roles: string[];
}): Promise<string> {
  // Assuming POST /Users returns the created user with an ID
  const response = await axiosClientAdmin.post<{ user: { id: string } }>('/Users', userData);
  return response.data.user.id;
}

export function CreateUserModal({
  isOpen,
  onClose,
  teamId,
  onUserCreatedAndAdded,
  initialFirstName = '',
  initialLastName = '',
  initialEmail = '',
}: CreateUserModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const { toast } = useToast();

  const mutationOptions = {
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'An unexpected error occurred.',
      });
    },
  };

  const addUserToTeamMutation = useMutation<void, Error, { userId: string }>({
    mutationFn: ({ userId }) => addUserToTeam({ teamId, userId }),
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'New user created and added to the team!',
      });
      onUserCreatedAndAdded(); // This should trigger refresh in TeamAgentsTab
      onClose(); // Close this modal
    },
    ...mutationOptions,
  });

  const createUserMutation = useMutation<
    string,
    Error,
    { firstName: string; lastName: string; email: string; roles: string[] }
  >({
    mutationFn: createUser,
    onSuccess: (newUserId) => {
      addUserToTeamMutation.mutate({ userId: newUserId });
    },
    ...mutationOptions,
  });

  useEffect(() => {
    if (isOpen) {
      setFirstName(initialFirstName ?? '');
      setLastName(initialLastName ?? '');
      setEmail(initialEmail ?? '');
    } else {
      setFirstName('');
      setLastName('');
      setEmail('');
    }
  }, [isOpen, initialFirstName, initialLastName, initialEmail]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName || !lastName || !email) {
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        description: 'Please fill in all fields.',
      });
      return;
    }
    createUserMutation.mutate({ firstName, lastName, email, roles: ['Monitoring'] });
  };

  if (!isOpen) return null;

  const isLoading = createUserMutation.isPending || addUserToTeamMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="border-white/20 bg-[#1a1a1a] text-white sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-white/90">Create New User</DialogTitle>
          <div className="text-white/70">
            Enter the details for the new user. They will be automatically added to the current
            team.
          </div>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <DialogDescription>
            <div className="py-2">
              <Label htmlFor="firstName" className="text-white/80">
                First Name
              </Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter first name"
                disabled={isLoading}
              />
            </div>
            <div className="py-2">
              <Label htmlFor="lastName" className="text-white/80">
                Last Name
              </Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter last name"
                disabled={isLoading}
              />
            </div>
            <div className="pt-2">
              <Label htmlFor="email" className="text-white/80">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                disabled={isLoading}
              />
            </div>
          </DialogDescription>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !firstName || !lastName || !email}
              className="bg-lime text-black hover:bg-lime/90 disabled:opacity-50"
            >
              {isLoading ? <Spinner size="small" /> : 'Create and Add User'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
